import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Check,
  Sparkles,
  Calculator,
  FileText,
  LayoutDashboard,
  Wallet,
  ArrowRight,
  Loader2,
  Copy,
  CheckCircle2,
  Mail,
} from 'lucide-react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { MapleLeaf } from '@/components/ui/MapleLeaf'

interface UserData {
  email: string
  position: number
  editToken: string
}

const FEATURE_OPTIONS = [
  {
    id: 'acb',
    label: 'Perfect ACB + superficial loss rule',
    description: 'Automatic cost basis tracking with superficial loss detection',
    icon: Calculator,
    color: 'from-emerald-500 to-teal-600',
  },
  {
    id: 'tax',
    label: 'One-click CRA tax reports',
    description: 'Generate Schedule 3 and complete tax forms instantly',
    icon: FileText,
    color: 'from-amber-500 to-orange-600',
  },
  {
    id: 'dashboard',
    label: 'Beautiful real-time dashboard',
    description: 'Track your portfolio with live prices and performance',
    icon: LayoutDashboard,
    color: 'from-violet-500 to-purple-600',
  },
  {
    id: 'wallet',
    label: 'Track custom wallet',
    description: 'Connect and track external wallets beyond Newton & Shakepay',
    icon: Wallet,
    color: 'from-sky-500 to-blue-600',
  },
]

export function ThanksPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Get user data from URL params and sessionStorage (FIX #5: token no longer in URL)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [liveCount, setLiveCount] = useState(0)
  const [featureText, setFeatureText] = useState('')
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [copied, setCopied] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [showMagicLink, setShowMagicLink] = useState(false)

  // Parse URL params and get token from sessionStorage
  useEffect(() => {
    const email = searchParams.get('email')
    const position = searchParams.get('position')
    // FIX #5: Get edit token from sessionStorage instead of URL
    const editToken = sessionStorage.getItem('waitlist_edit_token')

    if (email && position && editToken) {
      setUserData({
        email: decodeURIComponent(email),
        position: parseInt(position, 10),
        editToken,
      })
      // Clear the token from sessionStorage after reading (optional - keeps it secure)
      // sessionStorage.removeItem('waitlist_edit_token')
    } else if (email && position) {
      // Allow page to work without token (just won't have edit link)
      setUserData({
        email: decodeURIComponent(email),
        position: parseInt(position, 10),
        editToken: '',
      })
    } else {
      // No valid params, redirect to home
      navigate('/')
    }
  }, [searchParams, navigate])

  // Fetch live count
  const fetchCount = useCallback(async () => {
    try {
      const response = await fetch('/api/waitlist')
      const data = await response.json()
      setLiveCount(data.count || 0)
    } catch {
      // Fallback to position
      if (userData) {
        setLiveCount(userData.position)
      }
    }
  }, [userData])

  useEffect(() => {
    fetchCount()
    // Poll for live updates every 10 seconds
    const interval = setInterval(fetchCount, 10000)
    return () => clearInterval(interval)
  }, [fetchCount])

  const toggleFeature = (id: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    )
  }

  const handleSubmit = async () => {
    if (!userData) return

    setIsSubmitting(true)

    try {
      await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userData.email,
          featureText,
          votedFeatures: selectedFeatures,
          editToken: userData.editToken,
        }),
      })

      setIsSubmitted(true)
      setShowMagicLink(true)

      // Try to send magic link email
      try {
        const emailResponse = await fetch('/api/send-magic-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userData.email,
            editToken: userData.editToken,
          }),
        })
        const emailData = await emailResponse.json()
        if (emailData.success && !emailData.fallback) {
          setEmailSent(true)
        }
      } catch {
        // Email failed, but feedback saved
      }
    } catch {
      // Fallback - still show success
      setIsSubmitted(true)
      setShowMagicLink(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const copyMagicLink = async () => {
    if (!userData) return
    const link = `${window.location.origin}/edit?token=${userData.editToken}`
    await navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!userData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold-400" />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Radial glow */}
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px]
                     bg-gradient-radial from-gold-500/10 via-gold-500/5 to-transparent
                     blur-3xl animate-pulse-slow"
        />

        {/* Floating maple leaves */}
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 left-[10%] opacity-10"
        >
          <MapleLeaf className="w-24 h-24 text-maple-500" />
        </motion.div>

        <motion.div
          animate={{
            y: [0, 15, 0],
            rotate: [0, -8, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute bottom-32 right-[15%] opacity-10"
        >
          <MapleLeaf className="w-16 h-16 text-gold-400" />
        </motion.div>

        {/* Grid pattern */}
        <div className="absolute inset-0 grid-pattern opacity-30" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 mb-6
                         bg-gradient-to-br from-profit-500 to-profit-600
                         rounded-full shadow-lg shadow-profit-500/30"
            >
              <Check className="w-10 h-10 text-white" strokeWidth={3} />
            </motion.div>

            {/* Position Counter */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-3 px-6 py-3
                            bg-midnight-800/60 backdrop-blur-xl
                            border border-gold-500/30 rounded-2xl">
                <Sparkles className="w-5 h-5 text-gold-400" />
                <span className="text-slate-300 font-medium">You are join</span>
                <motion.span
                  key={userData.position}
                  initial={{ scale: 1.3, color: '#f7d046' }}
                  animate={{ scale: 1, color: '#f7d046' }}
                  className="text-2xl font-bold font-mono text-gold-400"
                >
                  #{userData.position.toLocaleString()}
                </motion.span>
              </div>
            </motion.div>

            {/* Live Counter Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm
                        text-slate-400 bg-midnight-800/40 rounded-full"
            >
              <span className="w-2 h-2 bg-profit-500 rounded-full animate-pulse" />
              <span>{liveCount.toLocaleString()} people on the waitlist</span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-3xl md:text-4xl font-bold text-slate-100"
            >
              You're on the list, <span className="text-gold-400">{userData.email.split('@')[0]}</span>!
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-4 text-lg text-slate-400"
            >
              Help us build exactly what you need. Your input shapes the product.
            </motion.p>
          </motion.div>

          {/* Feedback Form */}
          <AnimatePresence mode="wait">
            {!isSubmitted ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: 0.7 }}
                className="space-y-8"
              >
                {/* Open Text Question */}
                <div
                  className="p-6 bg-midnight-800/40 backdrop-blur-lg
                            border border-slate-700/40 rounded-2xl"
                >
                  <label className="block text-lg font-semibold text-slate-100 mb-4">
                    What is the <span className="text-gold-400">#1 feature</span> you absolutely need
                    in a Newton + Shakepay portfolio tracker?
                  </label>
                  <textarea
                    value={featureText}
                    onChange={(e) => setFeatureText(e.target.value)}
                    placeholder="Tell us what would make this app a must-have for you..."
                    rows={4}
                    className={cn(
                      'w-full px-5 py-4 bg-midnight-900/60 border border-slate-700/50 rounded-xl',
                      'text-slate-100 placeholder-slate-500 text-base',
                      'focus:outline-none focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20',
                      'transition-all duration-200 resize-none'
                    )}
                  />
                </div>

                {/* Feature Voting */}
                <div className="space-y-4">
                  <p className="text-slate-400 text-center">
                    Or quick-vote on these popular requests (select all that apply):
                  </p>

                  <div className="grid gap-4">
                    {FEATURE_OPTIONS.map((feature, index) => {
                      const Icon = feature.icon
                      const isSelected = selectedFeatures.includes(feature.id)

                      return (
                        <motion.button
                          key={feature.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.8 + index * 0.1 }}
                          onClick={() => toggleFeature(feature.id)}
                          className={cn(
                            'group relative flex items-center gap-4 p-5',
                            'bg-midnight-800/40 backdrop-blur-lg rounded-xl',
                            'border-2 transition-all duration-300',
                            'text-left w-full',
                            isSelected
                              ? 'border-gold-500/50 bg-gold-500/5'
                              : 'border-slate-700/40 hover:border-slate-600/50'
                          )}
                        >
                          {/* Icon */}
                          <div
                            className={cn(
                              'flex-shrink-0 w-12 h-12 rounded-xl',
                              'flex items-center justify-center',
                              'bg-gradient-to-br',
                              feature.color,
                              'transition-transform duration-300',
                              isSelected ? 'scale-110' : 'group-hover:scale-105'
                            )}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h3
                              className={cn(
                                'font-semibold text-base transition-colors',
                                isSelected ? 'text-gold-400' : 'text-slate-100'
                              )}
                            >
                              {feature.label}
                            </h3>
                            <p className="text-sm text-slate-500 mt-0.5">{feature.description}</p>
                          </div>

                          {/* Checkbox */}
                          <div
                            className={cn(
                              'flex-shrink-0 w-6 h-6 rounded-md border-2',
                              'flex items-center justify-center transition-all',
                              isSelected
                                ? 'bg-gold-500 border-gold-500'
                                : 'border-slate-600 group-hover:border-slate-500'
                            )}
                          >
                            {isSelected && <Check className="w-4 h-4 text-midnight-950" strokeWidth={3} />}
                          </div>
                        </motion.button>
                      )
                    })}
                  </div>
                </div>

                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                  className="flex justify-center pt-4"
                >
                  <motion.button
                    onClick={handleSubmit}
                    disabled={isSubmitting || (!featureText.trim() && selectedFeatures.length === 0)}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={cn(
                      'px-10 py-4 text-lg font-bold rounded-xl',
                      'bg-gradient-to-r from-gold-500 to-gold-400 text-midnight-950',
                      'shadow-lg shadow-gold-500/30',
                      'hover:shadow-xl hover:shadow-gold-500/40',
                      'transition-all duration-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg',
                      'flex items-center gap-3'
                    )}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Submit My Wish List
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                </motion.div>

                {/* Skip Option */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="text-center text-sm text-slate-500"
                >
                  All fields are optional. Share what matters to you!
                </motion.p>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8 bg-midnight-800/40 backdrop-blur-lg
                          border border-profit-500/30 rounded-2xl text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  className="inline-flex items-center justify-center w-16 h-16 mb-6
                           bg-profit-500/20 rounded-full"
                >
                  <CheckCircle2 className="w-8 h-8 text-profit-400" />
                </motion.div>

                <h2 className="text-2xl font-bold text-slate-100 mb-3">
                  Thanks for your input!
                </h2>

                <p className="text-slate-400 mb-6">
                  Your feedback is saved. {emailSent ? "Check your email for a magic link to update anytime." : "Save this link to update your wish list later:"}
                </p>

                {/* Magic Link Section */}
                {showMagicLink && (
                  <div className="space-y-4">
                    {emailSent ? (
                      <div className="space-y-2 text-center">
                        <div className="flex items-center justify-center gap-2 text-profit-400">
                          <Mail className="w-5 h-5" />
                          <span>Magic link sent to {userData.email}</span>
                        </div>
                        <p className="text-sm text-slate-500">
                          Check your spam/junk folder if you don't see it within a few minutes.
                        </p>
                      </div>
                    ) : (
                      <div className="p-4 bg-midnight-900/60 rounded-xl">
                        <p className="text-xs text-slate-500 mb-2">Your personal edit link:</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-3 py-2 bg-midnight-950 rounded-lg text-sm text-gold-400 overflow-hidden text-ellipsis">
                            {window.location.origin}/edit?token={userData.editToken.slice(0, 8)}...
                          </code>
                          <button
                            onClick={copyMagicLink}
                            className={cn(
                              'px-4 py-2 rounded-lg font-medium text-sm transition-all',
                              copied
                                ? 'bg-profit-500 text-white'
                                : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                            )}
                          >
                            {copied ? (
                              <span className="flex items-center gap-1">
                                <Check className="w-4 h-4" />
                                Copied!
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Copy className="w-4 h-4" />
                                Copy
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Follow on X */}
                <motion.a
                  href="https://x.com/tracker_capital"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center gap-2 mt-6 px-5 py-2.5
                           bg-slate-800/60 hover:bg-slate-700/60
                           border border-slate-700/50 hover:border-slate-600/50
                           rounded-xl text-slate-300 hover:text-white transition-all"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Follow @tracker_capital for updates
                </motion.a>

                {/* Back to Home */}
                <motion.a
                  href="/"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="inline-flex items-center gap-2 mt-4 text-slate-400 hover:text-gold-400 transition-colors"
                >
                  Back to TrackerCapital
                </motion.a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
